/** Programmatic scrolls of the strip, which move smoothly unless the reader asks for less motion. */
export function scrollBehavior(): ScrollBehavior {
    const reduce =
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    return reduce ? 'auto' : 'smooth';
}

/** The strip's own inline padding, which a card scrolled to the start should clear. */
function scrollPaddingStart(track: HTMLElement): number {
    return Number.parseFloat(getComputedStyle(track).scrollPaddingInlineStart) || 0;
}

/** The card for an image, which is a direct child of the strip with the image id as its value. */
export function findCard(track: HTMLElement, imageId: string): HTMLElement | null {
    for (const child of Array.from(track.children)) {
        if (child instanceof HTMLButtonElement && child.value === imageId) {
            return child;
        }
    }
    return null;
}

const REVEAL_MARGIN_PX = 16;

/** Centres a card in the strip unless it is already fully in view. */
export function revealCard(track: HTMLElement, card: HTMLElement, behavior: ScrollBehavior): void {
    const left = card.offsetLeft;
    const right = left + card.offsetWidth;
    const viewLeft = track.scrollLeft;
    const viewRight = viewLeft + track.clientWidth;
    if (left >= viewLeft + REVEAL_MARGIN_PX && right <= viewRight - REVEAL_MARGIN_PX) {
        return;
    }
    track.scrollTo?.({ left: left - (track.clientWidth - card.offsetWidth) / 2, behavior });
}

/**
 * Scrolls a day's marker, and so its first card, to the start of the strip. False when the
 * strip holds no cards for that day.
 */
export function scrollToDay(track: HTMLElement, day: string): boolean {
    const marker = Array.from(track.querySelectorAll<HTMLElement>('[data-day]')).find(
        (element) => element.dataset.day === day
    );
    if (!marker) {
        return false;
    }
    track.scrollTo?.({
        left: marker.offsetLeft - scrollPaddingStart(track),
        behavior: scrollBehavior(),
    });
    return true;
}

/** One page of the strip, most of its width so the reader keeps a card of context. */
export function scrollPage(track: HTMLElement, direction: -1 | 1): void {
    track.scrollBy?.({ left: direction * track.clientWidth * 0.8, behavior: scrollBehavior() });
}
